// 配信先プロファイル一覧 / 編集(設計書 §3.4.3)

import { useState } from 'react';
import { ApiClientError } from '../api/client';
import {
  PLATFORM_LABELS,
  useDeleteProfile,
  useSetDefaultProfile,
  useStreamingProfiles,
  type Platform,
  type StreamingProfile,
} from '../api/streaming-profiles';
import { AppLayout } from '../components/AppLayout';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Modal } from '../components/ui/Modal';
import { ProfileForm } from './streaming-profiles/ProfileForm';

type EditorMode = { kind: 'closed' } | { kind: 'create' } | { kind: 'edit'; profile: StreamingProfile };

export function StreamingProfilesPage() {
  const profilesQuery = useStreamingProfiles();
  const setDefault = useSetDefaultProfile();
  const deleteMutation = useDeleteProfile();
  const [editor, setEditor] = useState<EditorMode>({ kind: 'closed' });
  const [confirmDelete, setConfirmDelete] = useState<StreamingProfile | null>(null);

  return (
    <AppLayout
      title="配信先プロファイル"
      description="RTMP 配信先(FC2 ライブ / YouTube 等)を管理します。"
      actions={
        <div style={{ minWidth: 160 }}>
          <Button onClick={() => setEditor({ kind: 'create' })}>+ 新規追加</Button>
        </div>
      }
    >
      {profilesQuery.isLoading && (
        <Card>
          <p style={{ margin: 0, color: 'var(--color-text-secondary)' }}>読み込み中…</p>
        </Card>
      )}

      {profilesQuery.error && (
        <Card>
          <p style={{ margin: 0, color: 'var(--color-error-text)' }}>
            {profilesQuery.error instanceof ApiClientError
              ? profilesQuery.error.message
              : '読み込みに失敗しました'}
          </p>
        </Card>
      )}

      {profilesQuery.data && profilesQuery.data.length === 0 && (
        <Card>
          <p style={{ margin: 0, color: 'var(--color-text-secondary)' }}>
            まだ配信先プロファイルがありません。「+ 新規追加」から作成してください。
          </p>
        </Card>
      )}

      {profilesQuery.data?.map((profile) => (
        <ProfileRow
          key={profile.ulid}
          profile={profile}
          onSetDefault={() => setDefault.mutate(profile.ulid)}
          onEdit={() => setEditor({ kind: 'edit', profile })}
          onDelete={() => setConfirmDelete(profile)}
          settingDefault={setDefault.isPending && setDefault.variables === profile.ulid}
        />
      ))}

      <Modal
        open={editor.kind !== 'closed'}
        title={editor.kind === 'edit' ? 'プロファイルを編集' : '新規プロファイル'}
        onClose={() => setEditor({ kind: 'closed' })}
        width={520}
      >
        {editor.kind === 'create' && (
          <ProfileForm mode="create" onClose={() => setEditor({ kind: 'closed' })} />
        )}
        {editor.kind === 'edit' && (
          <ProfileForm
            mode="edit"
            profile={editor.profile}
            onClose={() => setEditor({ kind: 'closed' })}
          />
        )}
      </Modal>

      <Modal
        open={confirmDelete !== null}
        title="プロファイルを削除しますか?"
        onClose={() => setConfirmDelete(null)}
        width={420}
        footer={
          <>
            <div style={{ minWidth: 100 }}>
              <Button variant="secondary" onClick={() => setConfirmDelete(null)}>
                キャンセル
              </Button>
            </div>
            <div style={{ minWidth: 100 }}>
              <Button
                variant="danger"
                disabled={deleteMutation.isPending}
                onClick={async () => {
                  if (!confirmDelete) return;
                  try {
                    await deleteMutation.mutateAsync(confirmDelete.ulid);
                    setConfirmDelete(null);
                  } catch {
                    // エラーは下のメッセージで表示
                  }
                }}
              >
                {deleteMutation.isPending ? '削除中…' : '削除する'}
              </Button>
            </div>
          </>
        }
      >
        <p style={{ margin: '0 0 8px', fontSize: '12px' }}>
          <strong>{confirmDelete?.name}</strong> を削除します。この操作は取り消せません。
        </p>
        <p style={{ margin: 0, fontSize: '11px', color: 'var(--color-text-tertiary)' }}>
          配信中のプロファイルは削除できません(409 が返ります)。
        </p>
        {deleteMutation.error && (
          <p
            style={{
              margin: '12px 0 0',
              fontSize: '11px',
              color: 'var(--color-error-text)',
            }}
          >
            {deleteMutation.error instanceof ApiClientError
              ? deleteMutation.error.message
              : '削除に失敗しました'}
          </p>
        )}
      </Modal>
    </AppLayout>
  );
}

interface ProfileRowProps {
  profile: StreamingProfile;
  onSetDefault: () => void;
  onEdit: () => void;
  onDelete: () => void;
  settingDefault: boolean;
}

function ProfileRow({ profile, onSetDefault, onEdit, onDelete, settingDefault }: ProfileRowProps) {
  return (
    <Card>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr auto',
          gap: 16,
          alignItems: 'flex-start',
        }}
      >
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <h3
              style={{
                margin: 0,
                fontSize: '14px',
                fontWeight: 500,
                color: 'var(--color-text-primary)',
              }}
            >
              {profile.name}
            </h3>
            <PlatformBadge platform={profile.platform} />
            {profile.is_default && <DefaultBadge />}
            {!profile.available_for_plan && <PlanRestrictedBadge />}
          </div>
          <dl
            style={{
              display: 'grid',
              gridTemplateColumns: '90px 1fr',
              rowGap: 4,
              margin: 0,
              fontSize: '11px',
            }}
          >
            <dt style={{ color: 'var(--color-text-tertiary)' }}>RTMP URL</dt>
            <dd style={{ margin: 0, color: 'var(--color-text-secondary)', wordBreak: 'break-all' }}>
              {profile.rtmp_url}
            </dd>
            <dt style={{ color: 'var(--color-text-tertiary)' }}>ストリームキー</dt>
            <dd
              style={{
                margin: 0,
                color: 'var(--color-text-secondary)',
                fontFamily: 'monospace',
              }}
            >
              {profile.stream_key_masked}
            </dd>
          </dl>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 130 }}>
          {!profile.is_default && (
            <Button
              variant="secondary"
              onClick={onSetDefault}
              disabled={settingDefault}
              style={{ padding: '6px 10px', fontSize: '11px' }}
            >
              {settingDefault ? '設定中…' : 'デフォルトにする'}
            </Button>
          )}
          <Button
            variant="secondary"
            onClick={onEdit}
            style={{ padding: '6px 10px', fontSize: '11px' }}
          >
            編集
          </Button>
          <Button
            variant="danger"
            onClick={onDelete}
            style={{ padding: '6px 10px', fontSize: '11px' }}
          >
            削除
          </Button>
        </div>
      </div>
    </Card>
  );
}

function PlatformBadge({ platform }: { platform: Platform }) {
  return (
    <span
      style={{
        fontSize: '9px',
        fontWeight: 500,
        color: 'var(--color-text-secondary)',
        background: 'rgba(255,255,255,0.04)',
        border: 'var(--border-default)',
        padding: '2px 6px',
        borderRadius: '3px',
        letterSpacing: '0.03em',
      }}
    >
      {PLATFORM_LABELS[platform]}
    </span>
  );
}

function DefaultBadge() {
  return (
    <span
      style={{
        fontSize: '9px',
        fontWeight: 500,
        color: 'white',
        background: 'var(--gradient-cta)',
        padding: '2px 6px',
        borderRadius: '3px',
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
      }}
    >
      デフォルト
    </span>
  );
}

function PlanRestrictedBadge() {
  return (
    <span
      title="現在のプランでは利用できません"
      style={{
        fontSize: '9px',
        fontWeight: 500,
        color: 'var(--color-warning-text)',
        background: 'rgba(251, 191, 36, 0.1)',
        border: '0.5px solid rgba(251, 191, 36, 0.3)',
        padding: '2px 6px',
        borderRadius: '3px',
        letterSpacing: '0.03em',
      }}
    >
      Pro 限定
    </span>
  );
}
